import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInventory } from '../contexts/InventoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import { getAdminId } from '../utils/getAdminId';
import { resolveWedgeScan } from '../utils/resolveWedgeScan';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { BarcodeScanner } from '../components/ui/BarcodeScanner';
import { ExternalScanner } from '../components/ui/ExternalScanner';
import { RecentActivitySidebar } from '../components/shipments/RecentActivitySidebar';
import { QuantityStepper } from '../components/ui/QuantityStepper';
import { ItemPicker } from '../components/inventory/ItemPicker';
import { useOfflineGuard } from '../components/ui/OfflineBanner';
import {
  ensureDefaultLocations,
  fetchLocationQuantitiesForProducts,
  fetchShopifyLocationMappings,
  resolveWarehouseLocation,
  LocationQuantity
} from '../utils/locationInventory';
import {
  Package,
  TruckIcon,
  Plus,
  Trash2,
  Save,
  Printer,
  AlertTriangle,
  Camera,
  Zap,
  X,
  ArrowUp,
  Keyboard,
  ChevronDown,
  ChevronUp,
  ScanLine,
  Edit3,
  Clock,
  CheckCircle2,
  Calendar,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  BarChart3,
  Boxes,
  ArrowDownToLine,
  History,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { useProductAttributes } from '../hooks/use-product-attributes';

interface ShipmentItem {
  product_id: string | null;
  product_name: string;
  quantity: number;
  current_stock: number;
  new_stock: number;
  category?: string;
  location?: string;
  sku?: string;
  barcode?: string;
  reference_code?: string;
  is_new_item: boolean;
  color_id?: string;
  size_id?: string;
  animationKey?: string;
}

interface ScannedItemData {
  barcode: string;
  existingItem?: any;
  isNew: boolean;
}

interface UndoState {
  item: ShipmentItem;
  index: number;
}

interface ShipmentHistory {
  id: string;
  shipment_number: string;
  supplier_name: string;
  status: string;
  received_date: string;
  notes?: string;
  items_count?: number;
  total_quantity?: number;
  created_at: string;
}

type AddItemTab = 'scan' | 'manual';
type PageTab = 'receive' | 'history';

export const IncomingShipments: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { allItems, allItemsLoading, fetchAllItems, addItem, invalidateAllItemsCache } = useInventory();
  const { success, error: showError, info, warning, showUndoNotification } = useNotification();
  const { isNetworkOffline } = useOfflineGuard();
  const [loading, setLoading] = useState(false);
  const [shopifySync, setShopifySync] = useState<{ done: number; total: number } | null>(null);
  // Serializes background Shopify pushes so overlapping saves never interleave on the
  // shared `shopifySync` indicator or clear each other's progress prematurely.
  const shopifySyncChainRef = useRef<Promise<void>>(Promise.resolve());
  const [shipmentItems, setShipmentItems] = useState<ShipmentItem[]>([]);
  const [supplierName, setSupplierName] = useState('');
  const [notes, setNotes] = useState('');
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showExternalScanner, setShowExternalScanner] = useState(false);
  const [showScannedItemModal, setShowScannedItemModal] = useState(false);
  const [scannedData, setScannedData] = useState<ScannedItemData | null>(null);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [manualItemName, setManualItemName] = useState('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [itemCategory, setItemCategory] = useState('');
  const [itemLocation, setItemLocation] = useState('');
  const [itemBarcode, setItemBarcode] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [lastAddedKey, setLastAddedKey] = useState<string | null>(null);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [undoState, setUndoState] = useState<UndoState | null>(null);
  const [itemCountKey, setItemCountKey] = useState(0);
  const [activeTab, setActiveTab] = useState<AddItemTab>(() => {
    const saved = localStorage.getItem('incoming-shipments-tab');
    return (saved === 'scan' || saved === 'manual') ? saved : 'manual';
  });
  const [showStockPreview, setShowStockPreview] = useState(false);
  const [showReferenceSection, setShowReferenceSection] = useState(true);
  const [manualBarcode, setManualBarcode] = useState('');

  const [pageTab, setPageTab] = useState<PageTab>('receive');
  const [shipmentHistory, setShipmentHistory] = useState<ShipmentHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyItemsPerPage] = useState(10);
  const [todayStats, setTodayStats] = useState({ received: 0, items: 0, quantity: 0 });
  const [showShipmentDetail, setShowShipmentDetail] = useState<ShipmentHistory | null>(null);
  const [shipmentDetailItems, setShipmentDetailItems] = useState<any[]>([]);
  const [availableLocations, setAvailableLocations] = useState<{ id: string; name: string }[]>([]);
  const [warehouseLocation, setWarehouseLocation] = useState<{ id: string; name: string } | null>(null);
  const [productLocationQtys, setProductLocationQtys] = useState<Record<string, LocationQuantity[]>>({});

  const quantityInputRef = useRef<HTMLInputElement>(null);

  const canModify = user?.role !== 'viewer';
  const adminId = getAdminId(user);

  const { colors, sizes } = useProductAttributes(adminId);

  const loadAllProductLocationQtys = useCallback(async () => {
    if (!adminId || allItems.length === 0) return;
    const productIds = allItems.map(item => item.id);
    const qtysMap = await fetchLocationQuantitiesForProducts(productIds, adminId);
    const result: Record<string, LocationQuantity[]> = {};
    qtysMap.forEach((qtys, productId) => {
      result[productId] = qtys;
    });
    setProductLocationQtys(result);
  }, [adminId, allItems]);

  useEffect(() => {
    if (adminId) {
      ensureDefaultLocations(adminId).then(setAvailableLocations);
      resolveWarehouseLocation(adminId).then(setWarehouseLocation);
    }
  }, [adminId]);

  useEffect(() => {
    if (adminId && allItems.length > 0 && availableLocations.length > 0) {
      loadAllProductLocationQtys();
    }
  }, [adminId, allItems.length, availableLocations.length, loadAllProductLocationQtys]);

  const getWarehouseQuantity = useCallback(
    (productId: string): number => {
      if (!warehouseLocation) return 0;
      const locQtys = productLocationQtys[productId];
      if (!locQtys) return 0;
      const wh = locQtys.find(q => q.locationId === warehouseLocation.id);
      return wh?.quantity ?? 0;
    },
    [warehouseLocation, productLocationQtys]
  );

  useEffect(() => {
    localStorage.setItem('incoming-shipments-tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (adminId) {
      fetchShipmentHistory();
      fetchTodayStats();
      fetchAllItems();
    }
  }, [adminId, fetchAllItems]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (shipmentItems.length > 0 && supplierName.trim()) {
          handleSaveShipment();
        } else if (shipmentItems.length === 0) {
          warning('Add items to the shipment first');
        } else if (!supplierName.trim()) {
          warning('Enter a supplier name first');
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (selectedProduct && quantity && quantity > 0) {
          addItemToShipment();
        }
      }

      if (e.key === 'Escape') {
        if (showCameraScanner) setShowCameraScanner(false);
        if (showExternalScanner) setShowExternalScanner(false);
        if (showScannedItemModal) handleCancelScannedItem();
        if (showClearConfirm) setShowClearConfirm(false);
        if (showKeyboardShortcuts) setShowKeyboardShortcuts(false);
        if (showShipmentDetail) setShowShipmentDetail(null);
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && undoState) {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProduct, quantity, shipmentItems, supplierName, showCameraScanner, showExternalScanner, showScannedItemModal, showClearConfirm, showKeyboardShortcuts, undoState, showShipmentDetail]);

  const fetchShipmentHistory = async () => {
    if (!adminId) return;
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('incoming_shipments')
        .select(`
          *,
          shipment_items (received_quantity)
        `)
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const enriched: ShipmentHistory[] = (data || []).map((shipment: any) => {
        const items = shipment.shipment_items || [];
        const { shipment_items: _items, ...rest } = shipment;
        return {
          ...rest,
          items_count: items.length,
          total_quantity: items.reduce(
            (sum: number, item: { received_quantity?: number }) => sum + (item.received_quantity || 0),
            0
          )
        };
      });

      setShipmentHistory(enriched);
    } catch (err) {
      console.error('Error fetching shipment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchTodayStats = async () => {
    if (!adminId) return;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: todayShipments, error } = await supabase
        .from('incoming_shipments')
        .select(`
          id,
          shipment_items (received_quantity)
        `)
        .eq('admin_id', adminId)
        .gte('received_date', today.toISOString());

      if (error) throw error;

      let totalItems = 0;
      let totalQuantity = 0;
      todayShipments?.forEach(shipment => {
        const items = shipment.shipment_items || [];
        totalItems += items.length;
        totalQuantity += items.reduce(
          (sum, item) => sum + (item.received_quantity || 0),
          0
        );
      });

      setTodayStats({
        received: todayShipments?.length || 0,
        items: totalItems,
        quantity: totalQuantity
      });
    } catch (err) {
      console.error('Error fetching today stats:', err);
    }
  };

  const fetchShipmentDetails = async (shipmentId: string) => {
    try {
      const { data, error } = await supabase
        .from('shipment_items')
        .select(`
          *,
          product:products(name, category, sku, barcode)
        `)
        .eq('shipment_id', shipmentId);

      if (error) throw error;
      setShipmentDetailItems(data || []);
    } catch (err) {
      console.error('Error fetching shipment details:', err);
      setShipmentDetailItems([]);
    }
  };

  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '';
  const getColorHex = (colorId?: string) => colors.find(c => c.id === colorId)?.hex_code || '';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '';

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [allItems]);

  const selectedProductIds = new Set(shipmentItems.map(si => si.product_id).filter(Boolean));

  const selectedProductData = selectedProduct
    ? allItems.find(p => p.id === selectedProduct)
    : null;

  const safetyCategories = [
    'General',
    'Flammable',
    'Toxic',
    'Corrosive',
    'Explosive',
    'Oxidizing',
    'Compressed Gas',
    'Biohazard',
    'Radioactive'
  ];

  const filteredHistory = useMemo(() => {
    if (!historySearch) return shipmentHistory;
    const search = historySearch.toLowerCase();
    return shipmentHistory.filter(s =>
      s.shipment_number.toLowerCase().includes(search) ||
      s.supplier_name.toLowerCase().includes(search) ||
      s.notes?.toLowerCase().includes(search)
    );
  }, [shipmentHistory, historySearch]);

  const paginatedHistory = useMemo(() => {
    const start = (historyPage - 1) * historyItemsPerPage;
    return filteredHistory.slice(start, start + historyItemsPerPage);
  }, [filteredHistory, historyPage, historyItemsPerPage]);

  const totalHistoryPages = Math.ceil(filteredHistory.length / historyItemsPerPage);

  const handleScanResult = async (code: string) => {
    const existingItem = allItems.find(
      item => item.barcode === code || item.sku === code || item.batch === code
    );

    setScannedData({
      barcode: code,
      existingItem: existingItem || undefined,
      isNew: !existingItem
    });

    setShowCameraScanner(false);
    setShowExternalScanner(false);
    setShowScannedItemModal(true);

    if (existingItem) {
      setSelectedProduct(existingItem.id);
      setManualItemName('');
      setItemCategory(existingItem.category || '');
      setItemLocation(existingItem.location || '');
      info(`Found: ${existingItem.name}`);
    } else {
      setSelectedProduct('');
      setManualItemName(`Item ${code}`);
      setItemCategory('General');
      setItemLocation('');
      info('New item detected - enter details');
    }
    setItemBarcode(code);
    setQuantity('');
  };

  const handleManualBarcodeSubmit = async () => {
    const raw = manualBarcode.trim();
    if (!raw) return;

    const resolved = await resolveWedgeScan(adminId, raw);
    setManualBarcode('');

    if (resolved.kind === 'unknown' || resolved.kind === 'epc_error') {
      warning(resolved.message);
      return;
    }

    if (resolved.kind === 'epc') {
      if (resolved.integrityAlarm) {
        warning(resolved.message || 'DATA INTEGRITY ALARM — unit record and EPC disagree');
        return;
      }
      if (!resolved.unitFound) {
        warning(resolved.message || 'No unit record for this EPC. Using variant from EPC base.');
      }
      // Prefer inventory id match when ExternalScanner path would use barcode/sku
      await handleScanResult(resolved.value);
      return;
    }

    await handleScanResult(resolved.value);
  };

  const handleAddScannedItem = () => {
    addItemToShipment();
    setShowScannedItemModal(false);
    setScannedData(null);
  };

  const handleCancelScannedItem = () => {
    setShowScannedItemModal(false);
    setScannedData(null);
    setSelectedProduct('');
    setManualItemName('');
    setQuantity('');
    setItemCategory('');
    setItemLocation('');
    setItemBarcode('');
  };

  const addItemToShipment = () => {
    if (!quantity || quantity <= 0) {
      showError('Please enter a valid quantity');
      return;
    }

    const qty = Number(quantity);

    let itemData: ShipmentItem;
    const animationKey = `item-${Date.now()}`;

    if (selectedProduct) {
      const product = allItems.find(p => p.id === selectedProduct);
      if (!product) {
        showError('Product not found in inventory');
        return;
      }

      const whQty = getWarehouseQuantity(product.id);
      itemData = {
        product_id: product.id,
        product_name: product.name,
        quantity: qty,
        current_stock: whQty,
        new_stock: whQty + qty,
        category: product.category,
        location: product.location,
        sku: product.sku,
        barcode: product.barcode || undefined,
        is_new_item: false,
        color_id: product.color_id,
        size_id: product.size_id,
        animationKey
      };
    } else if (manualItemName) {
      if (!itemCategory) {
        showError('Please select a category for the new item');
        return;
      }

      itemData = {
        product_id: null,
        product_name: manualItemName,
        quantity: qty,
        current_stock: 0,
        new_stock: qty,
        category: itemCategory,
        location: itemLocation,
        barcode: itemBarcode || undefined,
        is_new_item: true,
        animationKey
      };
    } else {
      showError('Please select or enter an item');
      return;
    }

    setShipmentItems([...shipmentItems, itemData]);
    setLastAddedKey(animationKey);
    setItemCountKey(prev => prev + 1);

    success(`Added ${itemData.product_name} (${itemData.quantity} units)`);

    setSelectedProduct('');
    setManualItemName('');
    setQuantity('');
    setItemCategory('');
    setItemLocation('');
    setItemBarcode('');
    setShowStockPreview(false);

    setTimeout(() => setLastAddedKey(null), 500);
  };

  const removeItem = useCallback((index: number) => {
    const removedItem = shipmentItems[index];
    setRemovingIndex(index);

    setTimeout(() => {
      setShipmentItems(prev => prev.filter((_, i) => i !== index));
      setRemovingIndex(null);
      setItemCountKey(prev => prev + 1);

      setUndoState({ item: removedItem, index });

      showUndoNotification(
        `Removed "${removedItem.product_name}"`,
        () => {
          setShipmentItems(prev => {
            const newItems = [...prev];
            newItems.splice(index, 0, { ...removedItem, animationKey: `undo-${Date.now()}` });
            return newItems;
          });
          setUndoState(null);
          success('Item restored');
        }
      );
    }, 300);
  }, [shipmentItems, showUndoNotification, success]);

  const handleUndo = useCallback(() => {
    if (!undoState) return;

    setShipmentItems(prev => {
      const newItems = [...prev];
      newItems.splice(undoState.index, 0, { ...undoState.item, animationKey: `undo-${Date.now()}` });
      return newItems;
    });
    setUndoState(null);
    success('Item restored');
  }, [undoState, success]);

  const updateItemQuantity = (index: number, newQty: number) => {
    const qty = newQty || 0;
    const updatedItems = [...shipmentItems];
    updatedItems[index].quantity = qty;
    updatedItems[index].new_stock = updatedItems[index].current_stock + qty;
    setShipmentItems(updatedItems);
  };

  // Push the (already-updated) warehouse stock to the warehouse-mapped Shopify
  // location only, per product/SKU. Uses the existing absolute push-location path
  // (idempotent: re-running converges Shopify to the app quantity, never double-counts
  // a receipt). Skips silently for tenants whose warehouse is not mapped to Shopify.
  //
  // Reliability (enterprise-grade): the server-side push is verification-heavy and
  // time-boxed, so large shipments are processed in small chunks across bounded
  // rounds. Each request is abortable so it can never hang. We track which SKUs have
  // PASSED (verified) and which are permanently skipped (no Shopify SKU); only SKUs
  // that did NOT pass are retried, and verified SKUs are never pushed again.
  const pushWarehouseToShopify = async (
    productIds: string[]
  ): Promise<{ skipped: boolean; passed: number; permanentlySkipped: number; failed: number }> => {
    if (!adminId || productIds.length === 0) return { skipped: true, passed: 0, permanentlySkipped: 0, failed: 0 };

    const warehouse = await resolveWarehouseLocation(adminId);
    if (!warehouse) return { skipped: true, passed: 0, permanentlySkipped: 0, failed: 0 };

    const mappings = await fetchShopifyLocationMappings(adminId);
    const isWarehouseMapped = mappings.some(m => m.product_location_id === warehouse.id);
    if (!isWarehouseMapped) return { skipped: true, passed: 0, permanentlySkipped: 0, failed: 0 };

    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    const allIds = Array.from(new Set(productIds));
    const total = allIds.length;
    const PUSH_CHUNK = 10;          // keeps each server call well under its 40s budget
    const REQUEST_TIMEOUT_MS = 70_000;
    const MAX_ROUNDS = 4;
    const ROUND_BACKOFF_MS = 1_500;

    const passed = new Set<string>();              // status 'verified' — never retried
    const permanentlySkipped = new Set<string>();  // no valid Shopify SKU — terminal, not an error

    setShopifySync({ done: 0, total });

    const callPushLocation = async (
      chunk: string[]
    ): Promise<{ results?: Array<{ productId: string; status: string }> } | null> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(`${baseUrl}/functions/v1/shopify-push-inventory/push-location`, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ productLocationId: warehouse.id, productIds: chunk }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (err) {
        console.error('push-location request failed (will retry not-passed SKUs):', err);
        return null;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      for (let round = 0; round < MAX_ROUNDS; round++) {
        // Only the SKUs that have not yet passed and are not permanently skipped.
        const pending = allIds.filter(id => !passed.has(id) && !permanentlySkipped.has(id));
        if (pending.length === 0) break;

        for (let i = 0; i < pending.length; i += PUSH_CHUNK) {
          const chunk = pending.slice(i, i + PUSH_CHUNK);
          const data = await callPushLocation(chunk);

          if (data) {
            const results = Array.isArray(data.results) ? data.results : [];
            for (const r of results) {
              if (r.status === 'verified') passed.add(r.productId);
              else if (r.status === 'skipped') permanentlySkipped.add(r.productId);
              // 'failed' / 'mismatch' (and any id the server left in `remaining`,
              // i.e. not present in results) stay pending for the next round.
            }
          }

          setShopifySync({ done: passed.size + permanentlySkipped.size, total });
        }

        const stillPending = allIds.some(id => !passed.has(id) && !permanentlySkipped.has(id));
        if (!stillPending) break;
        if (round < MAX_ROUNDS - 1) await new Promise(r => setTimeout(r, ROUND_BACKOFF_MS));
      }

      const failed = allIds.filter(id => !passed.has(id) && !permanentlySkipped.has(id)).length;
      return { skipped: false, passed: passed.size, permanentlySkipped: permanentlySkipped.size, failed };
    } finally {
      setShopifySync(null);
    }
  };

  const handleSaveShipment = async () => {
    if (isNetworkOffline) {
      showError('Cannot save shipment while offline');
      return;
    }

    if (!canModify) {
      showError('You do not have permission to save shipments');
      return;
    }

    if (!adminId) {
      showError('Unable to determine account for this shipment');
      return;
    }

    if (shipmentItems.length === 0) {
      showError('Add at least one item to the shipment');
      return;
    }

    if (!supplierName.trim()) {
      showError('Please enter a supplier name');
      return;
    }

    interface ProcessedShipmentItem {
      productId: string;
      receivedQty: number;
      expectedQty: number;
      isNew: boolean;
    }

    try {
      setLoading(true);

      const shipmentNumber = `IN-${Date.now()}`;
      const processedItems: ProcessedShipmentItem[] = [];
      const failures: string[] = [];

      // Create any brand-new products first (0 stock, no inventory rows). A product
      // created here without a shipment is harmless — stock is only applied by the
      // atomic RPC below — so there is no pre-persist inventory mutation to unwind.
      for (const item of shipmentItems) {
        const receivedQty = item.quantity;

        if (item.is_new_item) {
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substr(2, 9);
          const uniqueSku = `SKU-${timestamp}-${randomId}`;

          const newProduct = await addItem({
            name: item.product_name,
            sku: uniqueSku,
            barcode: item.barcode || '',
            category: item.category || 'General',
            quantity: 0,
            unit_price: 0,
            location: item.location
          } as any);

          if (!newProduct) {
            failures.push(item.product_name);
            continue;
          }

          processedItems.push({
            productId: newProduct.id,
            receivedQty,
            expectedQty: receivedQty,
            isNew: true
          });
        } else {
          if (!item.product_id) {
            failures.push(item.product_name);
            continue;
          }

          processedItems.push({
            productId: item.product_id,
            receivedQty,
            expectedQty: receivedQty,
            isNew: false
          });
        }
      }

      if (failures.length > 0) {
        showError(
          failures.length === shipmentItems.length
            ? 'Failed to create products. Shipment was not saved.'
            : `Failed to create products for: ${failures.join(', ')}. Shipment was not saved.`
        );
        invalidateAllItemsCache();
        await fetchAllItems(true);
        await loadAllProductLocationQtys();
        return;
      }

      const warehouse = await resolveWarehouseLocation(adminId);
      if (!warehouse) {
        showError('No warehouse location is configured. Please create a "Warehouse" location first.');
        return;
      }

      // Atomic: insert shipment + items and increment warehouse stock in one
      // transaction. Nothing is applied unless the whole shipment persists.
      const { data: recvResult, error: shipmentError } = await supabase.rpc('receive_shipment_atomic', {
        p_admin_id: adminId,
        p_user_id: user!.id,
        p_shipment_number: shipmentNumber,
        p_supplier_name: supplierName,
        p_notes: notes,
        p_warehouse_location_id: warehouse.id,
        p_items: processedItems.map(p => ({
          product_id: p.productId,
          received_qty: p.receivedQty,
          expected_qty: p.expectedQty
        }))
      });

      if (shipmentError) throw shipmentError;
      if (!recvResult?.success) {
        showError(`Failed to save shipment: ${recvResult?.error || 'unknown error'}`);
        invalidateAllItemsCache();
        await fetchAllItems(true);
        await loadAllProductLocationQtys();
        return;
      }

      const totalItems = processedItems.length;
      const totalQty = processedItems.reduce((sum, item) => sum + item.receivedQty, 0);

      success(`Shipment ${shipmentNumber} saved! ${totalItems} items (+${totalQty} units) added to inventory`);

      // Existing products only — newly created items have no Shopify variant yet.
      const shopifyPushIds = processedItems
        .filter(p => !p.isNew)
        .map(p => p.productId);

      setShipmentItems([]);
      setSupplierName('');
      setNotes('');
      invalidateAllItemsCache();
      await fetchAllItems(true);
      await loadAllProductLocationQtys();
      fetchShipmentHistory();
      fetchTodayStats();

      // Push the updated warehouse stock to the warehouse-mapped Shopify location.
      // The shipment is already persisted and authoritative; a Shopify hiccup here
      // must never roll it back. We do NOT await this: the Save button is released
      // immediately and the sync runs in the background with its own progress
      // indicator and a final summary. Only SKUs that did not pass are retried.
      //
      // Saves can overlap (a large sync runs for minutes while the user enters and
      // saves another shipment), so we serialize pushes on a chain ref: each run waits
      // for the previous one to settle before starting, which keeps the single
      // `shopifySync` indicator accurate and prevents one run clearing another's progress.
      shopifySyncChainRef.current = shopifySyncChainRef.current
        .catch(() => undefined)
        .then(async () => {
          try {
            const pushResult = await pushWarehouseToShopify(shopifyPushIds);
            if (!pushResult.skipped) {
              if (pushResult.failed > 0) {
                warning(
                  `${pushResult.failed} item(s) did not sync to Shopify and need control in Sync Status.`
                );
              } else if (pushResult.passed > 0) {
                info(`Synced ${pushResult.passed} item(s) to the Shopify warehouse location.`);
              }
            }
          } catch (pushErr) {
            console.error('Shopify warehouse push failed:', pushErr);
            warning('The Shopify sync did not complete. It can be retried from Sync Status.');
          }
        });
    } catch (err) {
      console.error('Error saving shipment:', err);
      showError('Failed to save shipment. Please try again.');
      invalidateAllItemsCache();
      await fetchAllItems(true);
      await loadAllProductLocationQtys();
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    setShipmentItems([]);
    setSupplierName('');
    setNotes('');
    setShowClearConfirm(false);
    info('Shipment cleared');
  };

  const getStockLevelColor = (stock: number) => {
    if (stock <= 10) return 'text-red-600 dark:text-red-400';
    if (stock <= 25) return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-900 dark:text-gray-100';
  };

  const openShipmentDetail = async (shipment: ShipmentHistory) => {
    setShowShipmentDetail(shipment);
    await fetchShipmentDetails(shipment.id);
  };

  if (!canModify) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-300">
                View Only Mode
              </h3>
              <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                You have viewer access. Contact your administrator to create shipments.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl shadow-lg">
                <ArrowDownToLine className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('shipments.incoming.title')}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('dashboard.receiveNewStock')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowKeyboardShortcuts(true)}
                className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors hidden sm:flex"
                title="Keyboard shortcuts"
              >
                <Keyboard className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
              <button
                onClick={() => { fetchShipmentHistory(); fetchTodayStats(); }}
                className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
              <Button variant="secondary" onClick={() => window.print()} className="hidden sm:flex">
                <Printer className="h-4 w-4 mr-2" />
                {t('action.print')}
              </Button>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 pb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-teal-100 dark:border-teal-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-800/50 rounded-lg">
                  <TruckIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{todayStats.received}</p>
                  <p className="text-xs text-teal-600/70 dark:text-teal-400/70">Received Today</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                  <Boxes className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{todayStats.items}</p>
                  <p className="text-xs text-blue-600/70 dark:text-blue-400/70">Items Added</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl p-4 border border-emerald-100 dark:border-emerald-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">+{todayStats.quantity}</p>
                  <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">Units Added</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg">
                  <History className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{shipmentHistory.length}</p>
                  <p className="text-xs text-amber-600/70 dark:text-amber-400/70">Total Shipments</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setPageTab('receive')}
              className={`px-4 py-3 text-sm font-medium transition-all relative ${
                pageTab === 'receive'
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                <span>Receive Stock</span>
                {shipmentItems.length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-full">
                    {shipmentItems.length}
                  </span>
                )}
              </div>
              {pageTab === 'receive' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
              )}
            </button>
            <button
              onClick={() => setPageTab('history')}
              className={`px-4 py-3 text-sm font-medium transition-all relative ${
                pageTab === 'history'
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="h-4 w-4" />
                <span>Shipment History</span>
              </div>
              {pageTab === 'history' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6 pb-32 lg:pb-6">
        {pageTab === 'receive' ? (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setShowReferenceSection(!showReferenceSection)}
                className="w-full p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Shipment Details
                  </h2>
                  {!showReferenceSection && supplierName && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      - {supplierName}
                    </span>
                  )}
                </div>
                {showReferenceSection ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              <div className={`transition-all duration-300 overflow-hidden ${showReferenceSection ? 'max-h-48' : 'max-h-0'}`}>
                <div className="p-4 pt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={t('common.supplier')}
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Enter supplier name..."
                    required
                  />
                  <Input
                    label={t('common.notes')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('scan')}
                    className={`flex-1 px-4 py-3.5 text-sm font-medium transition-all relative ${
                      activeTab === 'scan'
                        ? 'text-teal-600 dark:text-teal-400 bg-teal-50/50 dark:bg-teal-900/10'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <ScanLine className="h-4 w-4" />
                      <span>Scan Barcode</span>
                    </div>
                    {activeTab === 'scan' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500 dark:bg-teal-400" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('manual')}
                    className={`flex-1 px-4 py-3.5 text-sm font-medium transition-all relative ${
                      activeTab === 'manual'
                        ? 'text-teal-600 dark:text-teal-400 bg-teal-50/50 dark:bg-teal-900/10'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Edit3 className="h-4 w-4" />
                      <span>Select Item</span>
                    </div>
                    {activeTab === 'manual' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500 dark:bg-teal-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className={`transition-opacity duration-200 ${activeTab === 'scan' ? 'block' : 'hidden'}`}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => setShowCameraScanner(true)}
                        className="min-h-[72px] flex items-center justify-center gap-3 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600 rounded-xl transition-all hover:shadow-md"
                      >
                        <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                          <Camera className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-left">
                          <span className="font-semibold text-blue-700 dark:text-blue-300 block">Camera Scanner</span>
                          <span className="text-xs text-blue-600/70 dark:text-blue-400/70">Use device camera</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setShowExternalScanner(true)}
                        className="min-h-[72px] flex items-center justify-center gap-3 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 hover:border-green-300 dark:hover:border-green-600 rounded-xl transition-all hover:shadow-md"
                      >
                        <div className="p-2 bg-green-100 dark:bg-green-800/50 rounded-lg">
                          <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="text-left">
                          <span className="font-semibold text-green-700 dark:text-green-300 block">External Scanner</span>
                          <span className="text-xs text-green-600/70 dark:text-green-400/70">USB/Bluetooth device</span>
                        </div>
                      </button>
                    </div>

                    <div className="relative">
                      <Input
                        label="Enter Barcode Manually"
                        value={manualBarcode}
                        onChange={(e) => setManualBarcode(e.target.value)}
                        placeholder="Type or paste barcode..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleManualBarcodeSubmit();
                          }
                        }}
                      />
                      {manualBarcode && (
                        <button
                          onClick={handleManualBarcodeSubmit}
                          className="absolute right-2 top-[34px] px-3 py-1.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors"
                        >
                          Go
                        </button>
                      )}
                    </div>

                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        Quick Tips
                      </h4>
                      <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1.5">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          Use Camera Scanner for phone/tablet cameras
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                          Use External Scanner for USB/Bluetooth devices
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                          Or type the barcode directly above
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className={`transition-opacity duration-200 ${activeTab === 'manual' ? 'block' : 'hidden'}`}>
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1 lg:flex-[2]">
                      {allItemsLoading ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                          <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-4" />
                          <p className="text-gray-500 dark:text-gray-400">Loading inventory ({allItems.length} items)...</p>
                        </div>
                      ) : (
                        <ItemPicker
                          items={allItems}
                          colors={colors}
                          sizes={sizes}
                          allCategories={allCategories}
                          selectedItemId={selectedProduct || null}
                          excludedItemIds={selectedProductIds}
                          onSelect={(item) => {
                            setSelectedProduct(item.id);
                            setManualItemName('');
                            setItemCategory(item.category || '');
                            setItemLocation(item.location || '');
                            setItemBarcode('');
                          }}
                          onClear={() => {
                            setSelectedProduct('');
                            setItemCategory('');
                            setItemLocation('');
                            setItemBarcode('');
                          }}
                          inlineMode={true}
                        />
                      )}
                    </div>

                    <div className="lg:w-72 xl:w-80 flex-shrink-0">
                      <div className="lg:sticky lg:top-4 space-y-3">
                        {selectedProductData ? (
                          <div className="p-4 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 border border-teal-200 dark:border-teal-700 rounded-xl">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 bg-teal-100 dark:bg-teal-800/50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Package className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                                  {selectedProductData.name}
                                </div>
                                {selectedProductData.reference_code && (
                                  <div className="text-xs text-teal-600 dark:text-teal-400 font-medium truncate">
                                    {selectedProductData.reference_code}
                                  </div>
                                )}
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Current: {getWarehouseQuantity(selectedProductData.id)} in stock
                                </div>
                              </div>
                            </div>
                            {(getColorName(selectedProductData.color_id) || getSizeName(selectedProductData.size_id)) && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {getColorName(selectedProductData.color_id) && (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    {getColorHex(selectedProductData.color_id) && (
                                      <span
                                        className="w-2.5 h-2.5 rounded-full border border-amber-300 dark:border-amber-600"
                                        style={{ backgroundColor: getColorHex(selectedProductData.color_id) }}
                                      />
                                    )}
                                    {getColorName(selectedProductData.color_id)}
                                  </span>
                                )}
                                {getSizeName(selectedProductData.size_id) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                    {getSizeName(selectedProductData.size_id)}
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm p-2.5 bg-white dark:bg-gray-800 rounded-lg border border-teal-100 dark:border-teal-800">
                              <span className="text-gray-600 dark:text-gray-400">After receiving:</span>
                              <span className="font-bold text-green-600 dark:text-green-400">
                                {quantity ? `${getWarehouseQuantity(selectedProductData.id) + Number(quantity)} (+${quantity})` : '-'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl text-center">
                            <Package className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">Select an item from the list</p>
                          </div>
                        )}

                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Quantity to Receive
                          </label>
                          <input
                            ref={quantityInputRef}
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                setQuantity('');
                              } else {
                                const num = parseInt(val, 10);
                                if (!isNaN(num) && num >= 0) {
                                  setQuantity(num);
                                }
                              }
                            }}
                            min={1}
                            className="w-full h-14 text-center text-2xl font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 transition-all text-gray-900 dark:text-gray-100"
                            placeholder="0"
                          />
                        </div>

                        <Button
                          onClick={addItemToShipment}
                          className="w-full min-h-[52px] text-sm font-semibold"
                          disabled={!selectedProduct || !quantity || quantity <= 0}
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Add to Shipment
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {shipmentItems.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 border-b border-teal-100 dark:border-teal-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100 dark:bg-teal-800/50 rounded-lg">
                      <Package className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Current Shipment
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {shipmentItems.length} item{shipmentItems.length !== 1 ? 's' : ''} ready to receive
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Units</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">
                        +{shipmentItems.reduce((sum, item) => sum + item.quantity, 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('inventory.table.name')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('product.category')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Color / Size
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Current
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <span className="text-green-600 dark:text-green-400">Receiving</span>
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <span className="text-green-600 dark:text-green-400">New Stock</span>
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('inventory.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {shipmentItems.map((item, index) => {
                        const colorName = getColorName(item.color_id);
                        const colorHex = getColorHex(item.color_id);
                        const sizeName = getSizeName(item.size_id);
                        const isNewlyAdded = item.animationKey === lastAddedKey;
                        const isRemoving = removingIndex === index;
                        return (
                          <tr
                            key={item.animationKey || index}
                            className={`transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                              isNewlyAdded ? 'animate-item-add' : ''
                            } ${isRemoving ? 'animate-item-remove' : ''}`}
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {item.product_name}
                                </div>
                                {item.is_new_item && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 mt-1">
                                    New Item
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {item.category ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                  {item.category}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap gap-1">
                                {colorName && (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    {colorHex && (
                                      <span
                                        className="w-2.5 h-2.5 rounded-full border border-amber-300 dark:border-amber-600"
                                        style={{ backgroundColor: colorHex }}
                                      />
                                    )}
                                    {colorName}
                                  </span>
                                )}
                                {sizeName && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                    {sizeName}
                                  </span>
                                )}
                                {!colorName && !sizeName && (
                                  <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <span className={`text-sm font-medium ${getStockLevelColor(item.current_stock)}`}>
                                {item.current_stock}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex justify-center">
                                <QuantityStepper
                                  value={item.quantity}
                                  onChange={(val) => updateItemQuantity(index, val)}
                                  min={1}
                                  size="sm"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <span className="inline-flex items-center gap-1 text-sm font-bold text-green-600 dark:text-green-400">
                                {item.new_stock}
                                <ArrowUp className="h-3.5 w-3.5" />
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => removeItem(index)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
                  {shipmentItems.map((item, index) => {
                    const colorName = getColorName(item.color_id);
                    const colorHex = getColorHex(item.color_id);
                    const sizeName = getSizeName(item.size_id);
                    const isNewlyAdded = item.animationKey === lastAddedKey;
                    const isRemoving = removingIndex === index;
                    return (
                      <div
                        key={item.animationKey || index}
                        className={`p-4 bg-white dark:bg-gray-900 ${
                          isNewlyAdded ? 'animate-item-add' : ''
                        } ${isRemoving ? 'animate-item-remove' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                              {item.product_name}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {item.is_new_item && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                  New
                                </span>
                              )}
                              {item.category && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                  {item.category}
                                </span>
                              )}
                              {colorName && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                  {colorHex && (
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorHex }} />
                                  )}
                                  {colorName}
                                </span>
                              )}
                              {sizeName && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                  {sizeName}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeItem(index)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors touch-target"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="text-center">
                            <div className={`text-lg font-bold ${getStockLevelColor(item.current_stock)}`}>
                              {item.current_stock}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
                          </div>
                          <div className="flex items-center">
                            <QuantityStepper
                              value={item.quantity}
                              onChange={(val) => updateItemQuantity(index, val)}
                              min={1}
                              size="sm"
                            />
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-600 dark:text-green-400 flex items-center justify-center gap-0.5">
                              {item.new_stock}
                              <ArrowUp className="h-4 w-4" />
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">New</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="hidden lg:flex p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 justify-between items-center">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {supplierName ? (
                      <span>Supplier: <strong className="text-gray-900 dark:text-gray-100">{supplierName}</strong></span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">Enter supplier name to save</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {shopifySync && (
                      <span className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Syncing to Shopify {shopifySync.done}/{shopifySync.total}
                      </span>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => setShowClearConfirm(true)}
                    >
                      Clear All
                    </Button>
                    <Button
                      onClick={handleSaveShipment}
                      disabled={loading || !supplierName.trim()}
                      className="px-8"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? t('message.loading') : 'Save Shipment'}
                      <span className="text-xs ml-2 opacity-70 hidden sm:inline">(Ctrl+S)</span>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TruckIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No items added yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                  Start by scanning a barcode or selecting items manually to add them to your shipment
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={historySearch}
                      onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                      placeholder="Search shipments..."
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-blue-300 placeholder:text-gray-400 dark:placeholder:text-blue-400/60 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {historyLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Loading shipment history...</p>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <History className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {historySearch ? 'No matching shipments' : 'No shipment history'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {historySearch ? 'Try a different search term' : 'Received shipments will appear here'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800/50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Shipment
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Supplier
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Items
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Units
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {paginatedHistory.map((shipment) => (
                          <tr key={shipment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                                  <TruckIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {shipment.shipment_number}
                                  </div>
                                  {shipment.notes && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                                      {shipment.notes}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">{shipment.supplier_name}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Calendar className="h-4 w-4" />
                                {format(new Date(shipment.received_date || shipment.created_at), 'MMM d, yyyy')}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {shipment.items_count ?? 0}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                +{shipment.total_quantity ?? 0}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Received
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => openShipmentDetail(shipment)}
                                className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors"
                                title="View details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedHistory.map((shipment) => (
                      <div
                        key={shipment.id}
                        onClick={() => openShipmentDetail(shipment)}
                        className="p-4 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                              <TruckIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {shipment.shipment_number}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {shipment.supplier_name}
                              </div>
                            </div>
                          </div>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                            <CheckCircle2 className="h-3 w-3" />
                            Received
                          </span>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5" />
                            {format(new Date(shipment.received_date || shipment.created_at), 'MMM d, yyyy')}
                          </div>
                          <div className="flex items-center gap-3">
                            <span>{shipment.items_count ?? 0} items</span>
                            <span className="font-medium text-emerald-600 dark:text-emerald-400">
                              +{shipment.total_quantity ?? 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {totalHistoryPages > 1 && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Showing {((historyPage - 1) * historyItemsPerPage) + 1} to {Math.min(historyPage * historyItemsPerPage, filteredHistory.length)} of {filteredHistory.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                            disabled={historyPage === 1}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Page {historyPage} of {totalHistoryPages}
                          </span>
                          <button
                            onClick={() => setHistoryPage(p => Math.min(totalHistoryPages, p + 1))}
                            disabled={historyPage === totalHistoryPages}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {shipmentItems.length > 0 && pageTab === 'receive' && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg safe-bottom z-30">
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowClearConfirm(true)}
              className="flex-1 min-h-[52px]"
            >
              Clear
            </Button>
            <Button
              onClick={handleSaveShipment}
              disabled={loading || !supplierName.trim()}
              className="flex-[2] min-h-[52px]"
            >
              <Save className="h-5 w-5 mr-2" />
              {loading ? 'Saving...' : `Save (${shipmentItems.length} items)`}
            </Button>
          </div>
        </div>
      )}

      <BarcodeScanner
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onScan={handleScanResult}
        title="Scan Barcode - Camera"
      />

      <ExternalScanner
        isOpen={showExternalScanner}
        onClose={() => setShowExternalScanner(false)}
        onScan={handleScanResult}
        mode="incoming"
        title="Scan Barcode - External Device"
      />

      {showScannedItemModal && scannedData && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10 sm:pt-20 px-4">
          <div className="relative w-full max-w-2xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <ScanLine className="h-6 w-6" />
                  {scannedData.isNew ? 'New Item Detected' : 'Item Found'}
                </h3>
                <button
                  onClick={handleCancelScannedItem}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                      Barcode: {scannedData.barcode}
                    </p>
                    {scannedData.existingItem && (
                      <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                        Current Stock: <span className="font-semibold">{getWarehouseQuantity(scannedData.existingItem.id)}</span> {scannedData.existingItem.unit || 'units'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {scannedData.isNew ? (
                  <>
                    <Input
                      label={t('product.name')}
                      value={manualItemName}
                      onChange={(e) => setManualItemName(e.target.value)}
                      placeholder="Enter item name"
                      required
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('product.category')}
                        </label>
                        <select
                          value={itemCategory}
                          onChange={(e) => setItemCategory(e.target.value)}
                          className="input min-h-[44px]"
                          required
                        >
                          <option value="">{t('product.category')}</option>
                          {safetyCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <Input
                        label={t('inventory.table.location')}
                        value={itemLocation}
                        onChange={(e) => setItemLocation(e.target.value)}
                        placeholder={t('inventory.table.location')}
                      />
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Item Name:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {scannedData.existingItem.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Category:</span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {scannedData.existingItem.category || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Location:</span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {scannedData.existingItem.location || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Quantity to Receive
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setQuantity('');
                      } else {
                        const num = parseInt(val, 10);
                        if (!isNaN(num) && num >= 0) {
                          setQuantity(num);
                        }
                      }
                    }}
                    min={1}
                    className="w-full h-14 text-center text-2xl font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 transition-all text-gray-900 dark:text-gray-100"
                    placeholder="Enter quantity"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={handleCancelScannedItem}
                  className="flex-1 min-h-[48px]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddScannedItem}
                  className="flex-1 min-h-[48px]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Shipment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center px-4">
          <div className="relative w-full max-w-md p-6 shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Clear All Items?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This will remove all {shipmentItems.length} items from the current shipment. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 min-h-[48px]"
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleClearAll}
                  className="flex-1 min-h-[48px]"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showKeyboardShortcuts && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center px-4">
          <div className="relative w-full max-w-md p-6 shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Keyboard className="h-5 w-5" />
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowKeyboardShortcuts(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { keys: ['Ctrl', 'S'], action: 'Save shipment' },
                { keys: ['Ctrl', 'Enter'], action: 'Add item to shipment' },
                { keys: ['Ctrl', 'Z'], action: 'Undo last removed item' },
                { keys: ['Esc'], action: 'Close modals/scanners' },
              ].map((shortcut, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{shortcut.action}</span>
                  <div className="flex items-center gap-1">
                    {shortcut.keys.map((key, i) => (
                      <React.Fragment key={i}>
                        <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs font-mono text-gray-700 dark:text-gray-300 shadow-sm">
                          {key}
                        </kbd>
                        {i < shortcut.keys.length - 1 && <span className="text-gray-400">+</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Use Cmd instead of Ctrl on Mac
              </p>
            </div>
          </div>
        </div>
      )}

      {showShipmentDetail && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10 sm:pt-20 px-4">
          <div className="relative w-full max-w-2xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in overflow-hidden mb-10">
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    {showShipmentDetail.shipment_number}
                  </h3>
                  <p className="text-teal-100 text-sm mt-1">
                    {showShipmentDetail.supplier_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowShipmentDetail(null)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Received Date</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {format(new Date(showShipmentDetail.received_date || showShipmentDetail.created_at), 'PPP')}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</p>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Received
                  </span>
                </div>
              </div>

              {showShipmentDetail.notes && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{showShipmentDetail.notes}</p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Items Received</h4>
                  {shipmentDetailItems.length > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {shipmentDetailItems.length} items · +{shipmentDetailItems.reduce(
                        (sum, item) => sum + (item.received_quantity || 0),
                        0
                      )} units
                    </span>
                  )}
                </div>
                {shipmentDetailItems.length === 0 ? (
                  <div className="p-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                    <Package className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading items...</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Item</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qty</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {shipmentDetailItems.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {item.product?.name || 'Unknown Item'}
                              </div>
                              {item.product?.category && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {item.product.category}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                +{item.received_quantity}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex justify-end border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="secondary"
                onClick={() => setShowShipmentDetail(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {adminId && <RecentActivitySidebar adminId={adminId} />}
    </div>
  );
};
